import { EmotionCharacterButton } from "../Controls/EmotionCharacterButton";
import * as classNames from "classnames";
import { IKeyValue } from "motw_core/Entity/IKeyValue";
import * as React from "react";
import { Emotion } from "source/app/domain/Emotions/Emotion";
import { Emotions } from "source/app/domain/Emotions/Emotions";
import { ActiveEmotionDisplayCharacter } from "source/app/presentation/BottomBar/EmotionSelector/EmotionCharacter/ActiveEmotion/DisplayCharacter";
import { AddStatusMessage } from "source/app/presentation/MainPanel/StatusBar/StatusBar";
import { EStatusMessageType } from "source/app/presentation/MainPanel/StatusBar/StatusMessageType/StatusMessageType";
import { Animate } from "source/app/presentation/Shared/Animation/Animate";
import { Animations } from "source/app/presentation/Shared/Animation/Animations";
import { ImageWithOverlay } from "source/app/presentation/Shared/Image/ImageWithOverlay";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import { ShieldOrnaments } from "source/app/presentation/BottomBar/EmotionSelector/EmotionCharacter/ShieldOrnaments";
import { useFontSwitcher } from "source/app/presentation/Shared/Fonts/Hooks/useFontSwitcher";
import {
    Fonts,
    webkitFilterGlitchFix,
} from "source/app/presentation/Shared/Styles/SharedStyles";
import { MathUtils } from "source/app/services/Math/MathUtils";

interface IActiveEmotionCharacterInputs {
    emotion: Emotion;
    handleClick: () => void;
    handleLongPress?: () => void;
    keyValue: IKeyValue;
    isSaving: boolean;
}

export function ActiveEmotionCharacter({
    emotion,
    handleClick,
    handleLongPress,
    keyValue,
    isSaving,
}: IActiveEmotionCharacterInputs) {
    const [bottomPosition, setBottomPosition] = React.useState(emotion.Index);
    const useAnopenscrollFont = useFontSwitcher();

    const sepia = emotion.IsAtLeast(Emotions.Integrated) ? 0 : 1;
    const dropShadow = `drop-shadow(0px 0px ${2 + emotion.Index * 2}px ${
        emotion.hexColor
    })`;

    const shieldFilterCSS = emotion.shieldHueRotateDegrees
        ? `sepia(${sepia}) hue-rotate(${emotion.shieldHueRotateDegrees}deg) saturate(1.7) brightness(0.9) contrast(1.04) ${dropShadow}`
        : ``;

    const frameImageOptions = {
        imageLabel: `activeEmotionCharacter_frame_${emotion.character}`,
        imageWidth: 51,
        imageHeight: 81.6,
        imageUrl: "/images/icons/shield-gold-border.svg",
        imageCSS: {
            display: `inline-flex`,
            zIndex: 3,
            position: "absolute" as "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none" as "none",
        },
    };

    const shieldImageOptions = {
        imageLabel: `activeEmotionCharacter_shieldFill_${emotion.character}`,
        imageWidth: 43,
        imageHeight: 68.8,
        imageUrl: "/images/icons/shield.svg",
        imageCSS: {
            display: `inline-flex`,
            filter: shieldFilterCSS,
            zIndex: 1,
            position: "relative" as "relative",
            ...webkitFilterGlitchFix,
            transition: `all 1s`,
        },
    };

    const characterStyle = {
        fontSize: "32px",
        position: "relative" as "relative",
        bottom: `3px`,
        left: `1px`,
        display: `inline-block`,
        lineHeight: 1,
    };

    const bounceHeight =
        (emotion.Index + 1) * MathUtils.RandomNumberBetween(25, 35);

    return (
        <span
            onMouseEnter={() => setBottomPosition(bottomPosition + 3)}
            onMouseLeave={() => setBottomPosition(bottomPosition - 3)}
            style={{
                position: `relative`,
                transition: `all 0.2s ease`,
                bottom: `${bottomPosition}px`,
            }}
        >
            <Animate
                animation={Animations.Bounce3D(bounceHeight, 2000)}
                playImmediately
                playOnClick
            >
                <EmotionCharacterButton
                    isActive
                    handleClick={handleClick}
                    handleLongPress={handleLongPress}
                >
                    <ImageWithOverlay options={shieldImageOptions} />
                    <span
                        style={{
                            position: `absolute`,
                            inset: 0,
                            display: `inline-flex`,
                            alignItems: `center`,
                            justifyContent: `center`,
                            zIndex: 2,
                            pointerEvents: `none`,
                        }}
                    >
                        <span
                            className={classNames(StyleClass(
                                `activeEmotionCharacter_${emotion.character}`,
                                characterStyle
                            ))}
                            style={
                                useAnopenscrollFont
                                    ? undefined
                                    : Fonts.iMFell
                            }
                        >
                            <ActiveEmotionDisplayCharacter
                                keyValue={keyValue}
                                emotion={emotion}
                                isSaving={isSaving}
                            />
                        </span>
                    </span>
                    <ImageWithOverlay options={frameImageOptions} />
                    <ShieldOrnaments isActive />
                </EmotionCharacterButton>
            </Animate>
        </span>
    );
}
